import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
	CreatePlatformDTO,
	PlatformFiltersDTO,
	PlatformListResponseDTO,
	PlatformResponseDTO,
	UpdatePlatformDTO,
} from '@libs/dtos/platform/platform.dto';
import { map, Observable, of, shareReplay, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { fetchAllPages } from '../../utils/fetch-all-pages';

@Injectable({
	providedIn: 'root',
})
export class PlatformService {
	private readonly baseUrl = `${environment.pathBackend}/platforms`;

	// Cache for preloaded data
	private cachedPublicPlatforms: PlatformListResponseDTO | null = null;

	/** Every platform (admin catalog), shared by the screens that only need names/options */
	private allPlatforms$: Observable<PlatformResponseDTO[]> | null = null;

	constructor(private http: HttpClient) {}

	getPlatforms(params?: Partial<PlatformFiltersDTO>): Observable<PlatformListResponseDTO> {
		return this.http.get<PlatformListResponseDTO>(this.baseUrl, { params });
	}

	/**
	 * All platforms, requested once and shared (products list, product form, filters...).
	 * Invalidated by create/update/delete.
	 */
	getAllPlatforms(): Observable<PlatformResponseDTO[]> {
		if (!this.allPlatforms$) {
			this.allPlatforms$ = fetchAllPages((paging) =>
				this.http.get<PlatformListResponseDTO>(this.baseUrl, { params: paging }),
			).pipe(
				map((response) => response?.data ?? []),
				tap({
					error: () => {
						this.allPlatforms$ = null; // a failed load is retried next time
					},
				}),
				shareReplay(1),
			);
		}
		return this.allPlatforms$;
	}

	/** Every platform as select options ({ label: name, value: id }), from the shared list */
	getPlatformOptions(): Observable<{ label: string; value: string }[]> {
		return this.getAllPlatforms().pipe(
			map((platforms) => platforms.map((p) => ({ label: p.name, value: p.id.toString() }))),
		);
	}

	getPlatformById(id: string): Observable<PlatformResponseDTO> {
		return this.http.get<PlatformResponseDTO>(`${this.baseUrl}/${id}`);
	}

	createPlatform(data: CreatePlatformDTO): Observable<PlatformResponseDTO> {
		return this.http.post<PlatformResponseDTO>(this.baseUrl, data).pipe(tap(() => this.invalidate()));
	}

	updatePlatform(id: string, data: UpdatePlatformDTO): Observable<PlatformResponseDTO> {
		return this.http.put<PlatformResponseDTO>(`${this.baseUrl}/${id}`, data).pipe(tap(() => this.invalidate()));
	}

	deletePlatform(id: string): Observable<void> {
		return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(tap(() => this.invalidate()));
	}

	/** Deletes several platforms in one request */
	bulkDeletePlatforms(ids: number[]): Observable<{ affected: number }> {
		return this.http
			.post<{ affected: number }>(`${this.baseUrl}/bulk-delete`, { ids })
			.pipe(tap(() => this.invalidate()));
	}

	// ==================== PUBLIC METHODS (no authentication) ====================

	getPublicPlatforms(params?: Partial<PlatformFiltersDTO>): Observable<PlatformListResponseDTO> {
		// Return cached data if available
		if (this.cachedPublicPlatforms) {
			return of(this.cachedPublicPlatforms);
		}

		// Every page: the API serves 10 per page by default and the store must show all of them
		return fetchAllPages((paging) =>
			this.http.get<PlatformListResponseDTO>(`${this.baseUrl}/public`, { params: { ...params, ...paging } }),
		).pipe(
			tap((response) => {
				this.cachedPublicPlatforms = response;
			}),
		);
	}

	/**
	 * Preload public platforms for faster access later
	 */
	preloadPublicPlatforms(): void {
		if (!this.cachedPublicPlatforms) {
			this.getPublicPlatforms().subscribe();
		}
	}

	/**
	 * Clear the cache
	 */
	clearCache(): void {
		this.cachedPublicPlatforms = null;
	}

	/** A platform changed: the next read goes to the API */
	private invalidate(): void {
		this.allPlatforms$ = null;
		this.cachedPublicPlatforms = null;
	}
}
